import {
	DateString,
	Highlight,
	HighlightedText,
	IconAndText,
	Space,
	PipeSeparator
} from "client/Components";
import { useHighlights } from "client/Features/Layout/TitleBar/GlobalSearch/ResultView/CategoryView/ItemView/useHighlights";
import { AnnouncementGlobalSearchResultItem, GlobalSearchScopeIdentifier } from "client/Api/TitleBar/GlobalSearch/globalSearch";
import { ReactElement, useMemo } from "react";
import { ItemLayout } from "./ItemLayout";
import {
	generateTrackingId,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import { NumberFormatter, useKeyboardListNavigationItem } from "client/Hooks";
import { useHubStatisticsFormatter } from "client/Features/Hub/useHubStatisticsFormatter";
import { hubItemKindToIcon, HubThumbnail } from "client/Features/Hub/HubThumbnail/HubThumbnail";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { TimeZone } from "client/DateAndTime/timezones";
import { localize } from "client_legacy/ndaw/culture";
import { HubSubtitleWithCategories } from "client/Features/Hub/HubSubtitleWithCategories";
import { ReactionStatistic, useReactionStatistic } from "client/Features/Reactions/useReactionStatistic";
import { useAnnouncementNavigationHandler } from "client/Features/Layout/TitleBar/GlobalSearch/Navigation/useAnnouncementNavigationHandler";
import { SearchListItem } from "../SearchListItem";
import { Key } from "client/Misc/commonTypes";

export type AnnouncementItemViewProps = {
	item: AnnouncementGlobalSearchResultItem;
	navigationIndex: number;
	itemScope: GlobalSearchScopeIdentifier;
	isMobile: boolean;
	timezone: TimeZone;
};

export const AnnouncementItemView = (props: AnnouncementItemViewProps): ReactElement => {
	const {
		item,
		isMobile,
		navigationIndex,
		itemScope,
		timezone
	} = props;

	const {
		id
	} = item.announcement;

	const executeHandler: () => void = useAnnouncementNavigationHandler({
		id,
		searchResult: item,
		itemScope
	});

	return (
		<SearchListItem
			onExecute={executeHandler}
			trackingId={generateTrackingId("announcement-item", `|${id}`)}
		>
			<AnnouncementItem
				item={item}
				isMobile={isMobile}
				timezone={timezone}
				navigationIndex={navigationIndex}
				onExecute={executeHandler}
			/>
		</SearchListItem>
	);
};
AnnouncementItemView.displayName = "AnnouncementItemView";

type AnnouncementItemProps = {
	item: AnnouncementGlobalSearchResultItem;
	isMobile: boolean;
	timezone: TimeZone;
	navigationIndex: number;
	onExecute: () => void;
};

const AnnouncementItem = (props: AnnouncementItemProps): ReactElement => {
	const {
		item,
		isMobile,
		timezone,
		navigationIndex,
		onExecute
	} = props;

	const {
		hitHighlights,
		announcement
	} = item;

	const {
		title,
		publicationStart,
		authorName,
		descriptionPreview,
		reactionCounters,
		commentsCount,
		media,
		categories
	} = announcement;

	const highlights: Map<string, Highlight> = useHighlights(hitHighlights);

	const statisticsFormatter: NumberFormatter = useHubStatisticsFormatter();
	const reactionStatistic: ReactionStatistic = useReactionStatistic(reactionCounters);

	const categoryNames = useMemo(
		(): readonly string[] => {
			return categories.map(x => x.name);
		},
		[categories]
	);

	const { ref, onKeyDown } = useKeyboardListNavigationItem({
		itemIndex: navigationIndex,
		onExecute,
		executeKey: [Key.Enter, Key.Space],
	});

	return (
		<ItemLayout
			trackingId="announcement-info"
			ref={ref}
			onKeyDown={onKeyDown}
			tabIndex={-1}
		>
			<ItemLayout.IconContainer>
				<ItemLayout.FontIcon
					icon={hubItemKindToIcon[HubItemKind.Announcement]}
				/>
			</ItemLayout.IconContainer>
			<ItemLayout.InfoWrapper>
				<ItemLayout.Title trackingId="title">
					<HighlightedText
						text={title}
						highlight={highlights.get("announcement.title")}
					/>
				</ItemLayout.Title>
				<ItemLayout.Info>
					<HubSubtitleWithCategories
						categoryNames={categoryNames}
					>
						<>
							<span
								{...setTrackingId("publication-date")}
							>
								<DateString
									value={publicationStart}
									format="RelativeTimestamp"
									timezone={timezone}
								/>
							</span>
							{isNotNil(authorName) && (
								<>
									<PipeSeparator />
									{localize("_(Edited)By")}
									<Space />
									<span
										{...setTrackingId("author")}
									>
										{authorName}
									</span>
								</>
							)}
						</>
					</HubSubtitleWithCategories>
				</ItemLayout.Info>
				<ItemLayout.Content>
					<HighlightedText
						text={descriptionPreview}
						highlight={highlights.get("announcement.descriptionPreview")}
					/>
				</ItemLayout.Content>
				<ItemLayout.Statistics>
					<IconAndText
						text={statisticsFormatter(reactionStatistic.total)}
					>
						<IconAndText.Icon
							value={reactionStatistic.icon}
							size="small"
						/>
					</IconAndText>
					<IconAndText
						text={statisticsFormatter(commentsCount)}
					>
						<IconAndText.Icon
							value="comment"
							size="small"
						/>
					</IconAndText>
				</ItemLayout.Statistics>
			</ItemLayout.InfoWrapper>
			{!isMobile && (
				<ItemLayout.Thumbnail>
					<HubThumbnail
						hubItemKind={HubItemKind.Announcement}
						media={media}
						title={title}
						mediaPlaceholderSize="medium"
					/>
				</ItemLayout.Thumbnail>
			)}
		</ItemLayout>
	);
};
AnnouncementItem.displayName = "AnnouncementItem";